import { Temporal } from '@js-temporal/polyfill';
import DateTimeUnit = Temporal.DateTimeUnit;

type RoundType = 'floor' | 'round';
type ComparisonType = 'gte' | 'gt';
type UtilDateType = string | Temporal.Instant | number | Temporal.ZonedDateTime | Date;
export class TimeUtil {
    static MILLISECONDS_IN_SECOND = 1000;
    static SECONDS_IN_MINUTE = 60;
    static MINUTES_PER_DAY = 24 * TimeUtil.SECONDS_IN_MINUTE;
    static MILLISECONDS_IN_MINUTE = TimeUtil.SECONDS_IN_MINUTE * TimeUtil.MILLISECONDS_IN_SECOND;
    static MILLISECONDS_IN_HOUR = TimeUtil.SECONDS_IN_MINUTE * TimeUtil.MILLISECONDS_IN_MINUTE;
    static MILLISECONDS_IN_DAY = 24 * TimeUtil.MILLISECONDS_IN_HOUR;

    static getMillisecondsFromSeconds(seconds: number): number {
        return seconds * this.MILLISECONDS_IN_SECOND;
    }

    static getMillisecondsFromMinutes(minutes: number): number {
        return minutes * this.MILLISECONDS_IN_MINUTE;
    }

    static getMillisecondsFromHours(hours: number): number {
        return hours * this.MILLISECONDS_IN_HOUR;
    }

    static getMillisecondsFromDays(days: number): number {
        return days * this.MILLISECONDS_IN_DAY;
    }

    static getSecondsLeftToTargetSecond(targetSecond: number): number {
        const currentSecond = new Date().getSeconds();
        return currentSecond > targetSecond
            ? this.SECONDS_IN_MINUTE - currentSecond + targetSecond
            : targetSecond - currentSecond;
    }

    /**
     * Converts a date from various formats to a Temporal.Instant.
     *
     * This method handles different types of date inputs and converts them to a Temporal.Instant object.
     *
     * @private
     * @static
     * @param {UtilDateType} date - The date to convert. Supported types:
     *   - `number`: Interpreted as epoch milliseconds.
     *   - `string`: Interpreted as an ISO 8601 string.
     *   - `Date`: Converted from the Date object's timestamp.
     *   - `Temporal.ZonedDateTime`: Converted to its instant.
     *   - `Temporal.Instant`: Returned as is.
     * @returns {Temporal.Instant} The converted Temporal.Instant.
     */
    private static convertDate(date: UtilDateType): Temporal.Instant {
        switch (true) {
            case typeof date === 'number':
                return Temporal.Instant.fromEpochMilliseconds(date as number);
            case typeof date === 'string':
                return Temporal.Instant.from(date as string);
            case date instanceof Date:
                return Temporal.Instant.fromEpochMilliseconds((date as Date).getTime());
            case date instanceof Temporal.ZonedDateTime:
                return (date as Temporal.ZonedDateTime).toInstant();
            default:
                return date as Temporal.Instant;
        }
    }

    /**
     * Checks if the time elapsed between startDate and endDate satisfies the specified comparison with the given amount in the specified unit.
     *
     * This method calculates the time difference between endDate and startDate, converts it to the specified unit,
     * applies the chosen rounding method, and compares it to the given amount using the specified comparison type.
     *
     * @static
     * @param {UtilDateType} startDate - The starting date. Must be earlier than or equal to endDate.
     * @param {UtilDateType} endDate - The ending date. Must be later than or equal to startDate.
     * @param {{ type: DateTimeUnit; amount: number }} time - The time unit and amount to compare.
     *   - `type`: The unit of time, e.g., 'day', 'hour', 'minute', etc.
     *   - `amount`: The number of units to check against.
     * @param {RoundType} [roundType='round'] - The rounding method to apply to the elapsed time.
     *   - `'round'`: Rounds to the nearest integer.
     *   - `'floor'`: Rounds down to the largest integer less than or equal to the value.
     * @param {'gte' | 'gt'} [comparisonType='gte'] - The comparison type.
     *   - `'gte'`: Greater than or equal to (>=).
     *   - `'gt'`: Strictly greater than (>).
     * @returns {boolean} True if the rounded elapsed time satisfies the comparison with the given amount, false otherwise.
     * @throws {RangeError} If startDate or endDate cannot be converted to a valid Temporal.Instant.
     * @example
     * const start = new Date('2023-01-01');
     * const end = new Date('2023-01-06');
     * const result = DateUtils.isElapsedTimeExceeded(start, end, { type: 'day', amount: 5 });
     * console.log(result); // true
     */
    static isElapsedTimeExceeded(
        startDate: UtilDateType,
        endDate: UtilDateType,
        time: { type: DateTimeUnit; amount: number },
        roundType: RoundType = 'round',
        comparisonType: ComparisonType = 'gte'
    ): boolean {
        const startInstant = this.convertDate(startDate);
        const endInstant = this.convertDate(endDate);

        if (Temporal.Instant.compare(endInstant, startInstant) < 0) {
            return false;
        }

        const elapsedTime = endInstant.since(startInstant);
        const totalInUnit = elapsedTime.total({ unit: time.type });
        const roundedTotal = Math[roundType](totalInUnit);
        return comparisonType === 'gte' ? roundedTotal >= time.amount : roundedTotal > time.amount;
    }

    /**
     * Checks if two dates fall within the same time unit (e.g., same minute, hour, or day).
     *
     * This method calculates the elapsed time between startDate and endDate in the specified unit,
     * and returns true if the difference is zero,
     * indicating the dates are in the same time unit.
     *
     * @static
     * @param {UtilDateType} startDate - The starting date.
     * @param {UtilDateType} endDate - The ending date.
     * @param {DateTimeUnit} timeUnitType - The unit of time to compare (e.g., 'minute', 'hour', 'day').
     * @returns {boolean} True if the dates are in the same time unit, false otherwise.
     * @throws {RangeError} If startDate or endDate cannot be converted to a valid Temporal.Instant.
     * @example
     * const start = new Date('2023-01-01T10:01:00');
     * const end = new Date('2023-01-01T10:01:59');
     * const result = DateUtils.isTimesAreSameInTimeUnit(start, end, 'minute');
     * console.log(result); // false
     */
    static isTimesAreSameInTimeUnit(
        startDate: UtilDateType,
        endDate: UtilDateType,
        timeUnitType: DateTimeUnit
    ): boolean {
        const startInstant = this.convertDate(startDate);
        const endInstant = this.convertDate(endDate);

        const elapsedTime = endInstant.since(startInstant);
        const totalInUnit = elapsedTime.total({ unit: timeUnitType });

        return totalInUnit < 0 && totalInUnit > -1;
    }

    static removeMilliseconds<T extends UtilDateType>(date: T): T {
        switch (true) {
            case typeof date === 'string': {
                const stringDate = date as string;
                const parsedDate = new Date(stringDate);
                parsedDate.setMilliseconds(0);
                return parsedDate.toISOString() as T;
            }

            case date instanceof Date: {
                const dateObj = date as Date;
                const newDate = new Date(dateObj);
                newDate.setMilliseconds(0);
                return newDate as T;
            }

            case typeof date === 'number': {
                const numberDate = date as number;
                return (Math.floor(numberDate / 1000) * 1000) as T;
            }

            case Temporal !== undefined && date instanceof Temporal.Instant: {
                const instantDate = date as Temporal.Instant;
                return instantDate.round({ smallestUnit: 'second' }) as T;
            }

            case Temporal !== undefined && date instanceof Temporal.ZonedDateTime: {
                const zonedDate = date as Temporal.ZonedDateTime;
                return zonedDate.round({ smallestUnit: 'second' }) as T;
            }
            default:
        }
    }
    /**
     * Checks if the time elapsed from the given date to now satisfies the specified comparison with the given amount.
     *
     * This method delegates to `isElapsedTimeExceeded`, using the current moment as the end date.
     *
     * @static
     * @param {UtilDateType} date - The date to check against the current moment.
     * @param {{ type: DateTimeUnit; amount: number }} time - The time unit and amount to compare.
     * @param {RoundType} [roundType='round'] - The rounding method ('round' or 'floor').
     * @param {'gte' | 'gt'} [comparisonType='gte'] - The comparison type ('gte' for >=, 'gt' for >).
     * @returns {boolean} True if the elapsed time satisfies the comparison, false otherwise.
     * @see isElapsedTimeExceeded
     * @example
     * const date = new Date('2023-01-01');
     * const result = DateUtils.isTimePassed(date, { type: 'day', amount: 5 });
     * console.log(result); // true if now is 2023-01-06 or later
     */
    static isTimePassed(
        date: UtilDateType,
        time: { type: DateTimeUnit; amount: number },
        roundType: RoundType = 'round',
        comparisonType: ComparisonType = 'gte'
    ): boolean {
        const now = Temporal.Now.instant();

        return this.isElapsedTimeExceeded(date, now, time, roundType, comparisonType);
    }

    static getDate(dateTime: string): string {
        const temporalDateTime = this.convertDate(dateTime);
        const timeZoneId = this.getTimeZoneId(dateTime);
        const zonedDateTime = temporalDateTime.toZonedDateTimeISO(timeZoneId);
        const plainDate = zonedDateTime.toPlainDate();
        return plainDate.toString();
    }

    static getTimeZoneId(dateTime: string): string {
        const zonedDateTime = Temporal.ZonedDateTime.from(dateTime);
        return zonedDateTime.timeZoneId;
    }

    static getTime(dateTime: string): string {
        const temporalDateTime: Temporal.Instant = this.convertDate(dateTime);
        const timeZoneId = this.getTimeZoneId(dateTime);
        const plainTime = temporalDateTime.toZonedDateTimeISO(timeZoneId).toPlainTime();
        return `${plainTime.hour.toString().padStart(2, '0')}:${plainTime.minute.toString().padStart(2, '0')}`;
    }

    /**
     * Formats a JavaScript Date object to ISO 8601 date format (yyyy-MM-dd).
     *
     * This method converts a Date object to a Temporal.PlainDate and returns its string representation
     * in the ISO 8601 format. The output is always in the format 'yyyy-MM-dd' with zero-padded values.
     *
     * @static
     * @param {Date | string} date - The date value to format. Can be a JavaScript Date object or a parseable date string.
     * @returns {string} The formatted date string in 'yyyy-MM-dd' format, or an empty string if date is null/undefined.
     */

    static formatDateToISO(date: Date | string): string {
        if (!date) return '';

        const parsedDate = typeof date === 'string' ? new Date(date) : date;

        if (Number.isNaN(parsedDate.getTime())) {
            return '';
        }

        return Temporal.PlainDate.from({
            year: parsedDate.getUTCFullYear(),
            month: parsedDate.getUTCMonth() + 1,
            day: parsedDate.getUTCDate()
        }).toString();
    }
}
